import React, { useContext, useEffect, useMemo, useState } from 'react';
import { getUpperDaysText } from 'utils/translations/i18n';
import { buildClassName } from 'utils/build-class-name';
import ListingStatus from 'components/listing-status';
import ListingTimestamp from 'components/listing-timestamp';
import ListingPrice from 'components/listing-price';
import SchemaOrg from 'components/schema-org';
import BathIcon from 'components/icon/bath-icon';
import BedIcon from 'components/icon/bed-icon';
import Button from 'components/control/button';
import SqftIcon from 'components/icon/sqft-icon';
import ClockIcon from 'components/icon/clock-icon';
import CameraIcon from 'components/icon/camera-icon';
import CarIcon from 'components/icon/car-icon';
import FireIcon from 'components/icon/fire-icon';
import SqftText from 'components/sqft-text';
import { useIsMobile } from 'hooks/use-size-class';
import { ModalContext } from 'contexts/modal';
import { useRouter } from 'next/router';
import AddressHeading from './address-heading';
import ListingPagePrice from './price';
import ListingOpenHouses from './open-houses';
import { listingPageModalIds } from 'constants/test-constants';
import styles from './style.module.scss';
import { TryScrambleText } from 'utils/text-scrambler';
import { useChat } from 'contexts/chat';
import ConditionalWrapper from 'components/conditional-wrapper';
import { useExperiment, useGrowthBook } from '@growthbook/growthbook-react';
import { trackEvent } from 'utils/google-tag-manager';
import { useUserContext } from 'contexts/user';
import { GTM_CLICK_ADDRESS_PAGE_MAIN_PVR_CTA, GTM_CLICK_ADDRESS_PAGE_PRICE_HISTORY_BUTTON,
  GTM_CLICK_ADDRESS_PAGE_VIRTUAL_TOUR_BUTTON, GTM_CLICK_LISTING_MODAL_ADDRESS_TEXT } from 'constants/events';
import { ThemeNames } from 'types/themes';
import Cookies from 'js-cookie';
import { FIRST_VISIT_TIMESTAMP_KEY, MLML_AGENT_COOKIE_NAME } from 'constants/cookies';
import MyLinkMyLeadCreate from 'components/my-link-my-lead-create';
import { useThemeContext } from 'contexts';
import { ExperimentVariants } from 'utils/types';
import storage from 'utils/storage';
import { getBuyerRepresentationText } from 'utils/buyer-info';
import { courtesyProviderIds } from 'constants/mlsProviderIds';
import { createProvidersComplianceApi, Disclaimer } from 'data/providers-compliance';

import type Listing from 'data/listing';
import type { IModalContext } from 'contexts/modal';
import type { Zoopraisal } from 'utils/zoopraisal';

interface Props {
  listing: Listing;
  isVisible: boolean;
  isModal?: boolean;
  isHistorical?: boolean;
  priceHistoryRef: React.RefObject<HTMLDivElement>;
  shouldScramble: boolean;
  zoopraisalData?: Zoopraisal | null;
  listings?: Listing[];
  showsInfoIcon?: boolean;
  isFromPriceHistory?: boolean;
  soldListingExperimentId?: ExperimentVariants | undefined;
}

export default function ListingContent({ listing, isVisible, isModal = false, priceHistoryRef, shouldScramble, zoopraisalData: zoopraisal, listings, showsInfoIcon, isFromPriceHistory = false, soldListingExperimentId = undefined }: Props) {
  const { bedrooms, position, bathrooms, sqftLabel, parking, virtualTourUrl, openHouses, price, providerId, brokerage, brokeragePhone } = listing;
  const { openModal } = useContext(ModalContext) as IModalContext;
  const { hasNavigatedFromInternalLink } = useUserContext();
  const { setPageContent } = useChat();
  const router = useRouter();
  const [timeRemainingEstimation, setTimeRemainingEstimation] = useState<string | null>();
  const isMobile = useIsMobile();
  const showPriceHistoryBtn = listings && listings.length > 1;
  const { themeName } = useThemeContext();
  const showsMLMLCreate = themeName && themeName === ThemeNames.EXP_REALTY_US && !!Cookies.get(MLML_AGENT_COOKIE_NAME);
  const [disclaimerInfo, setDisclaimerInfo] = useState<Disclaimer>();

  const { ready: isMobileWebPvrButtonsExpReady } = useGrowthBook();
  const isExperimentEnabled = !hasNavigatedFromInternalLink && listing.isAvailable && isMobile;
  const abcTestingPVRButtons = useExperiment({ key: 'mobile-web-pvr-buttons', variations: [0, 1], weights: [.5, .5], active: isExperimentEnabled });

  const isFirstVisit = useMemo(() => {
    const firstVisitTimestamp = storage.get(FIRST_VISIT_TIMESTAMP_KEY) as number;
    if (!firstVisitTimestamp) {
      storage.set(FIRST_VISIT_TIMESTAMP_KEY, Date.now());
      return true;
    } else if (firstVisitTimestamp) {
      const currentDate = Date.now();
      const timeDifference = currentDate - firstVisitTimestamp;
      const timeDifferenceDays = timeDifference / (1000 * 60 * 60 * 24);

      if (isExperimentEnabled && timeDifferenceDays < 30 && timeDifference > 1000) {
        storage.set(FIRST_VISIT_TIMESTAMP_KEY, Date.now());
        return false;
      } else {
        return true;
      }
    }
  }, [isExperimentEnabled]);

  useEffect(() => {
    const grabDisclaimerInfo = async () => {
    // If PrimeMLS, get the disclaimer info so we can display it here
      if (courtesyProviderIds.includes(providerId)) {
        const providerCompliance = createProvidersComplianceApi();
        const providerData = await providerCompliance.getDisclaimer(providerId.toString(), themeName, listing);
        setDisclaimerInfo(providerData);
      }
    };
    grabDisclaimerInfo();
  }, [listing, providerId, themeName]);

  useEffect(() => {
    // Run experiment for PVR buttons
    if (isMobileWebPvrButtonsExpReady && isExperimentEnabled && isMobile) {
      switch (abcTestingPVRButtons.variationId) {
      case ExperimentVariants.A:
        trackEvent('XpA_AddressStxBarView2');
        break;
      case ExperimentVariants.B:
        trackEvent('XpB_AddressStxBarView2');
        break;
      default:
        break;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobileWebPvrButtonsExpReady]);

  const openConnectionRequest = (isViewingRequest = false, eventName: string | null = null, customButtonText?: string, prefillData = {}) => {
    if (!eventName) {
      if (soldListingExperimentId === ExperimentVariants.A) trackEvent('XpA_AddressTakeATourButClk');
      trackEvent(GTM_CLICK_ADDRESS_PAGE_MAIN_PVR_CTA);
    }
    // Only track event if experiment event name is provided
    if (eventName ) {
      trackEvent(eventName);
      (eventName !== 'XpA_AddressTourButClk2' && eventName !== 'XpB_AddressTourButClk2' && eventName !== 'XpB_AddressLearnMoreButClk2' && eventName !== 'XpA_AddressLearnMoreButClk2') && trackEvent('XpAll_AddressButClk');
    }
    openModal('connection-request-popup', { listing, isViewingRequest, customButtonText, prefillData });
  };

  //alternate version of openConnectionRequest to use connection-request instead of connection-request-popup
  const openConnectionRequestMobile = (isViewingRequest = false, eventName: string | null = null, customButtonText?: string, prefillData = {}) => {
    if (!eventName) {
      trackEvent(GTM_CLICK_ADDRESS_PAGE_MAIN_PVR_CTA);
    }
    // Only track event if experiment event name is provided
    if (eventName ) {
      trackEvent(eventName);
      (eventName !== 'XpA_AddressTourButClk2' && eventName !== 'XpB_AddressTourButClk2' && eventName !== 'XpB_AddressLearnMoreButClk2' && eventName !== 'XpA_AddressLearnMoreButClk2') && trackEvent('XpAll_AddressButClk');
    }
    openModal('connection-request', { listing, isViewingRequest, customButtonText, prefillData });
  };

  const handleSoldExperimentClick = () => {
    openModal('home-appraisal-connection-request', { listing });
  };

  const handleSoldSimilarListings = () => {
    trackEvent('XpC_AddressHomeEstButClk');
    openAreaPage();
  };

  const openAreaPage = () => {
    const parts = router.asPath.split('/');
    // City area page
    let url = '/' + parts[1];
    // We have neighborhood in the URL
    if (parts.length > 3) {
      url += '/' + parts[2];
    }
    router.push(url);
  };

  useEffect(() => {
    getEstimatedTimeRemainingOnMarket();
    if (listing) {
      setPageContent({ type: 'Listing', content: listing, isPrivate: !isVisible });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const getEstimatedTimeRemainingOnMarket = async () => {
    if (zoopraisal) {
      const { daysOnMarket } = listing;
      const { averageDaysOnMarket } = zoopraisal;
      const timeRemaining = averageDaysOnMarket! - daysOnMarket!;
      if (timeRemaining <= 14 && timeRemaining > 0 && listing.isAvailable && !listing.isRental) {
        const timeRemainingVerbage = averageDaysOnMarket! - daysOnMarket! > 0 ? getUpperDaysText(averageDaysOnMarket! - daysOnMarket!) : 'Very Soon';

        setTimeRemainingEstimation(timeRemainingVerbage);
      }
    }
  };

  const scrollToPriceHistory = () => {
    if (priceHistoryRef.current) {
      priceHistoryRef.current.scrollIntoView({ behavior: 'smooth' });
      if (!isModal) {
        trackEvent(GTM_CLICK_ADDRESS_PAGE_PRICE_HISTORY_BUTTON);
      }
    }
  };

  const trackVirtualTourClick = () => {
    if (!isModal) {
      trackEvent(GTM_CLICK_ADDRESS_PAGE_VIRTUAL_TOUR_BUTTON);
    }
  };

  const buyerRepresentationText = getBuyerRepresentationText(listing.buyerInfo);
  return (
    <div className={styles.component}>
      {isMobile && <ListingStatus listing={listing} className={buildClassName(styles['listing-status'], styles['mobile-status'], isModal && styles['modal-status'])} />}
      <div className={styles.row}>
        <div className={styles.left}>
          <span className={buildClassName(styles['mobile-only'], styles['price-time-row'])}>
            {price === 0 ? (
              <>
                <span
                  className={styles['contact-for-price']}
                  onClick={() => openConnectionRequestMobile(true, null, 'Request Info', { requestViewing: false, isShowingAdditionalComments: true, sourceComments: "I'd like to view the price of this property." })}
                >
                  Contact for Price
                </span>
                <ListingTimestamp listing={listing} className={styles['listed-since']} isVisible={isVisible} shouldScramble={shouldScramble} />
              </>
            ) : (
              <>
                <ListingPagePrice listing={listing} isVisible={isVisible} />
                <ListingTimestamp listing={listing} className={styles['listed-since']} isVisible={isVisible} shouldScramble={shouldScramble} />
              </>
            )}
          </span>
          <SchemaOrg itemType="SingleFamilyResidence">
            <SchemaOrg itemProp="address" itemType="PostalAddress">
              <ConditionalWrapper condition={isModal} wrapper={children => <div onClick={() => trackEvent(GTM_CLICK_LISTING_MODAL_ADDRESS_TEXT)}>{children}</div>}>
                <AddressHeading listing={listing} itemProp="streetAddress" data-testid={listingPageModalIds.listingAddress} isVisible={isVisible} shouldScramble={shouldScramble} isFromPriceHistory={isFromPriceHistory} />
              </ConditionalWrapper>
              <div className={buildClassName(styles['status-row'], styles['desktop-only'])} data-testid={listingPageModalIds.modalListingStatus}>
                <ListingStatus listing={listing} className={buildClassName(isModal && styles['listing-status'])} />
              </div>
            </SchemaOrg>
            <SchemaOrg itemProp="geo" itemType="GeoCoordinates">
              <meta itemProp="latitude" content={position ? position.latitude.toString() : ''} />
              <meta itemProp="longitude" content={position ? position.longitude.toString() : ''} />
            </SchemaOrg>
          </SchemaOrg>
        </div>
        <div className={styles.right} data-testid={listingPageModalIds.modalPriceInfoSection}>
          <span className={buildClassName(styles['desktop-listing-time'])}>
            {price === 0 ? (
              <>
                <span
                  className={styles['contact-for-price']}
                  onClick={() => openConnectionRequest(true, null, 'Request Info', { requestViewing: false, isShowingAdditionalComments: true, sourceComments: "I'd like to view the price of this property." })}
                >
                  Contact for Price
                </span>
                <ListingTimestamp listing={listing} className={styles['listed-since']} isVisible={isVisible} shouldScramble={shouldScramble} />
              </>
            ) : (
              <>
                <ListingPrice listing={listing} isVisible={isVisible} testId={listingPageModalIds.listingPriceModal} shouldScramble={shouldScramble} showsInfoIcon={showsInfoIcon} />
                <ListingTimestamp listing={listing} className={styles['listed-since']} isVisible={isVisible} shouldScramble={shouldScramble} testId={listingPageModalIds.modalListingTimeStamp} />
              </>
            )}
          </span>
          {timeRemainingEstimation &&
            <div className={styles['time-remaining']}>
              <span>
                <FireIcon />
                Estimated to sell in:
              </span>
              <span className={buildClassName((!isVisible) && 'blur', styles['red-text'])}>
                {timeRemainingEstimation}
              </span>
            </div>
          }
          <div className={styles['price-virtual-tour-section']}>
            {showPriceHistoryBtn && <a onClick={scrollToPriceHistory} className={buildClassName(styles['button-wrapper'])}>
              <Button label='Price History' theme='secondary' Icon={ClockIcon} testId={listingPageModalIds.priceHistoryBtn} />
            </a>}
            {virtualTourUrl && isVisible &&
            <a onClick={trackVirtualTourClick} href={virtualTourUrl} className={styles['button-wrapper']} target="_blank" rel="nofollow noreferrer">
              <Button label='Virtual Tour' theme='secondary' Icon={CameraIcon} testId={listingPageModalIds.virtualTourBtn} />
            </a>
            }
          </div>
        </div>
      </div>
      <div className={`${styles.row} ${styles['aligned-row']}`}>
        <div className={buildClassName((!isVisible) && 'blur', styles['stats-container'])}>
          <span className={styles.stat} data-testid={listingPageModalIds.listingBedIcon}><BedIcon className={styles.bed} />{bedrooms}</span>
          <span className={styles.stat} data-testid={listingPageModalIds.listingBathrooms}><BathIcon className={styles.bath} />{bathrooms}</span>
          <span className={styles.stat} data-testid={listingPageModalIds.listingSqft}><SqftIcon className={styles.sqft} /><SqftText squareFootageLabel={TryScrambleText(sqftLabel, !isVisible) as string} render={formattedSqft => <>{formattedSqft}</>} /></span>
          <span className={styles.stat} data-testid={listingPageModalIds.listingCar}><CarIcon className={styles.car} />{parking || 0}</span>
        </div>
      </div>
      {courtesyProviderIds.includes(providerId) &&
        <div className={styles['courtesy-container']}>
          <p className={styles.courtesy}>{disclaimerInfo?.CourtesyOf}</p>
          {listing.isSold && buyerRepresentationText &&
              <p className={styles.courtesy}>{buyerRepresentationText}</p>
          }
        </div>
      }

      <ListingOpenHouses openHouses={openHouses} />

      {isMobile && showsMLMLCreate ?
        <div className={styles['pvr-cta-wrapper']}>
          <MyLinkMyLeadCreate buttonClassName={styles['pvr-cta-button']} listing={listing} />
        </div>
        :
        <>
          {/* Show experiment buttons for Mobile PVR */}
          {(isMobile && isExperimentEnabled && !hasNavigatedFromInternalLink && !soldListingExperimentId && isFirstVisit) && (
            <div className={styles['pvr-cta-wrapper']}>
              {abcTestingPVRButtons.variationId == ExperimentVariants.A && (
                <Button
                  label="Take a Tour"
                  theme="primary"
                  className={styles['pvr-cta-button']}
                  onClick={() => {
                    openConnectionRequest(true, 'XpA_AddressTourButClk2');
                    trackEvent('XpAll_AddressButClk2');
                  }}
                  testId={listingPageModalIds.takeTourBtn}
                />
              )}
              {abcTestingPVRButtons.variationId == ExperimentVariants.B && (
                <>
                  <Button
                    label="Take a Tour"
                    theme="primary"
                    className={styles['pvr-cta-button']}
                    onClick={() => {
                      openConnectionRequest(true, 'XpB_AddressTourButClk2');
                      trackEvent('XpAll_AddressButClk2');
                    }}
                    testId={listingPageModalIds.takeTourBtn}
                  />
                  <Button
                    label="Learn More"
                    theme="primary"
                    className={buildClassName(styles['pvr-cta-button'], styles.white)}
                    onClick={() => {
                      openConnectionRequest(false, 'XpB_AddressLearnMoreButClk2', 'Learn More');
                      trackEvent('XpAll_AddressButClk2');
                    }}
                  />
                </>
              )}
            </div>
          )}

          {/* Show default button */}
          {(isMobile && !isExperimentEnabled && soldListingExperimentId == ExperimentVariants.A) && (
            <div className={styles['pvr-cta-wrapper']}>
              <Button
                label="Take a Tour"
                theme="primary"
                className={styles['pvr-cta-button']}
                onClick={() => openConnectionRequest(true)}
                testId={listingPageModalIds.takeTourBtn}
              />
            </div>
          )}

          {/* Show experiment buttons for Sold Listing */}
          {isMobile && soldListingExperimentId == ExperimentVariants.B && (
            <div className={styles['pvr-cta-wrapper']}>
              <Button
                label="View Similar Listings For Sale"
                theme="primary"
                className={styles['pvr-cta-button']}
                onClick={handleSoldSimilarListings}
              />
            </div>
          )}

          {isMobile && soldListingExperimentId == ExperimentVariants.C && (
            <div className={styles['pvr-cta-wrapper']}>
              <Button
                label="Get a Home Estimate for Free"
                theme="primary"
                className={styles['pvr-cta-button']}
                onClick={handleSoldExperimentClick}
              />
            </div>
          )}
        </>
      }
    </div>
  );
}
